import {
  OrderHistoryReceipt,
  OvenCookHistoryItem,
  ReviewSummary,
} from '@tovala/browser-apis-combinedapi'
import { useEffect, useRef, useState } from 'react'

import { getMostRecentTermMealsToRate, getTermsToRate } from 'utils/terms'

import { isRatingBannerPreviewed } from './previews'

const AUTO_CLOSE_AFTER_SUBMIT_MS = 5000

export function useMostRecentTermMealsToRate({
  cookHistory,
  mealReviewSummaries,
  orderHistoryReceipts,
}: {
  cookHistory: OvenCookHistoryItem[] | ''
  mealReviewSummaries: ReviewSummary | undefined
  orderHistoryReceipts: OrderHistoryReceipt[]
}) {
  const [isPreview] = useState(() =>
    isRatingBannerPreviewed({ search: window.location.search }),
  )

  const getMealsToRate = ({
    includeReviewedMeals,
  }: {
    includeReviewedMeals: boolean
  }) =>
    getMostRecentTermMealsToRate({
      ignoreDeliveryWindow: isPreview,
      termsToRate: getTermsToRate({
        cookHistory,
        includeReviewedMeals,
        mealReviewSummaries,
        orderHistoryReceipts,
      }),
    })

  const unratedMealsToRate = getMealsToRate({ includeReviewedMeals: false })

  // Test accounts often have every past meal rated already, so a previewed
  // banner falls back to rated meals rather than showing nothing.
  if (isPreview && !unratedMealsToRate) {
    return getMealsToRate({ includeReviewedMeals: true })
  }

  return unratedMealsToRate
}

export function useAutoCloseAfterSubmit({
  hasSubmitted,
  onAutoClose,
}: {
  hasSubmitted: boolean
  onAutoClose: () => void
}) {
  const onAutoCloseRef = useRef(onAutoClose)

  useEffect(() => {
    onAutoCloseRef.current = onAutoClose
  })

  useEffect(() => {
    if (!hasSubmitted) {
      return
    }

    const autoCloseTimeoutID = setTimeout(
      () => onAutoCloseRef.current(),
      AUTO_CLOSE_AFTER_SUBMIT_MS,
    )

    return () => {
      clearTimeout(autoCloseTimeoutID)
    }
  }, [hasSubmitted])
}
